import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { ActionButton, SectionContainer } from "./HomePrimitives";
import { PREMIUM } from "./homeContent";
import { BODY_SMALL, HEADING_2 } from "./homeType";

/** The two groups carry different tints, and so do their IP-count chips. */
const GROUP_TONE = {
  green: marketingPalette.premiumPanel,
  blue: marketingPalette.premiumPanelBlue,
};

export default function PremiumSection() {
  return (
    <SectionContainer padding="bottom">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          rowGap: "32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            rowGap: "8px",
            width: "70%",
            mx: "auto",
            textAlign: "center",
            [NOT_WIDE]: { width: 1 },
          }}
        >
          <Typography component="h2" sx={HEADING_2}>
            {PREMIUM.heading}
          </Typography>
          <Typography component="p" sx={BODY_SMALL}>
            {PREMIUM.body}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            width: 1,
            [MID]: { display: "flex", flexDirection: "column" },
          }}
        >
          {PREMIUM.groups.map((group) => (
            <Box
              key={group.title}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                p: "1.5rem",
                borderRadius: "12px",
                backgroundColor: GROUP_TONE[group.tone],
                [TINY]: { p: "1rem" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box
                  component="img"
                  src={group.icon}
                  alt=""
                  loading="lazy"
                  sx={{ width: "2rem", height: "2rem" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <Box
                    sx={{
                      color: marketingPalette.ink,
                      textTransform: "uppercase",
                      fontSize: 17,
                      fontWeight: 600,
                      lineHeight: "120%",
                    }}
                  >
                    {group.title}
                  </Box>
                  <Box sx={{ fontSize: 15, WebkitTextStrokeWidth: "0.1px" }}>
                    {group.subtitle}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  // Auto-FILL, not auto-fit: the panel goes back to two columns
                  // at 767 because it has just gone full-bleed, with no step of
                  // its own in between.
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(min(100%, 16rem), 1fr))",
                  gap: "16px",
                }}
              >
                {group.cards.map((card) => (
                  <Box
                    key={card.title}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1.5rem",
                      p: "1.25rem",
                      borderRadius: "12px",
                      backgroundColor: "background.default",
                      boxShadow: "0 4px 12px -6px #ABD4B7",
                      [TINY]: { p: "0.9rem" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <Box
                        component="img"
                        src={card.icon}
                        alt=""
                        loading="lazy"
                        sx={{ width: "2.5rem", height: "2.5rem" }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <Box
                          sx={{
                            color: marketingPalette.ink,
                            fontSize: 19,
                            fontWeight: 500,
                            lineHeight: "120%",
                          }}
                        >
                          {card.title}
                        </Box>
                        <Box
                          sx={{
                            opacity: 0.56,
                            color: marketingPalette.ink,
                            fontSize: 15,
                          }}
                        >
                          {card.subtitle}
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "1rem",
                      }}
                    >
                      <Box
                        sx={{
                          p: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          backgroundColor: GROUP_TONE[group.tone],
                          color: marketingPalette.ink,
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      >
                        {card.ips}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "4px",
                        }}
                      >
                        <Box
                          sx={{
                            color: marketingPalette.ink,
                            fontSize: 34,
                            fontWeight: 500,
                            lineHeight: "36px",
                          }}
                        >
                          {card.unitPrice}
                        </Box>
                        <Box sx={{ fontSize: 16, lineHeight: "28px" }}>
                          {card.unit}
                        </Box>
                      </Box>
                      {/* One line, not two: the note sits beside the total. */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <Box
                          sx={{
                            color: marketingPalette.ink,
                            fontSize: "1rem",
                            fontWeight: 500,
                            lineHeight: "1.25rem",
                          }}
                        >
                          {card.total}
                        </Box>
                        <Box
                          sx={{
                            color: "rgba(79, 92, 101, 0.7)",
                            fontSize: "0.75rem",
                            lineHeight: "1.25rem",
                          }}
                        >
                          {card.totalNote}
                        </Box>
                      </Box>
                    </Box>
                    {/* `.button_new.w-button`: the label alone. */}
                    <ActionButton
                      cta={card.cta}
                      arrow={false}
                      sx={{ mt: "auto" }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </SectionContainer>
  );
}
